import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Paperclip, Mic, ArrowLeft, AlertTriangle, TrendingUp, Plus, Clock, User, Ticket, Shield, Check, RotateCcw, ArrowUpRight, MessageCircle, UserPlus, MoreVertical, Trash2, X } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { TICKETS_DATA, TICKETS_CHAT_RESPONSES } from '../../data/ticketsData';

const PRIORITY_STYLES = {
  dark: {
    P1: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/20' },
    P2: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20' },
    P3: { bg: 'bg-slate-500/10', text: 'text-slate-500', border: 'border-slate-500/20' },
  },
  light: {
    P1: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
    P2: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    P3: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  },
};

const ACTION_ICONS = {
  'Reply': MessageCircle, 'Escalate': ArrowUpRight, 'Resolve': Check, 'Assign': UserPlus,
  'Reopen': RotateCcw, 'Follow Up': MessageCircle, 'Close': X, 'Delete': Trash2,
};

const TicketCard = ({ ticket, isDark, onAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const ps = (isDark ? PRIORITY_STYLES.dark : PRIORITY_STYLES.light)[ticket.priority];
  const isUrgent = ticket.slaUrgent || ticket.slaBreached;
  const isResolved = ticket.stage === 'Resolved';
  const cardBg = isResolved
    ? (isDark ? 'bg-emerald-950/10' : 'bg-emerald-50/30')
    : isUrgent ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = isResolved
    ? (isDark ? 'border-emerald-500/10' : 'border-emerald-200/40')
    : isUrgent ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Build kebab menu items: visible actions + Resolve + Delete
  const visibleActions = ticket.actions.slice(0, 2);
  const menuActions = [...ticket.actions];
  if (!menuActions.includes('Resolve') && !isResolved) menuActions.push('Resolve');
  if (!menuActions.includes('Delete')) menuActions.push('Delete');

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-2.5 mb-2 relative`} data-testid={`ticket-card-${ticket.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ps.bg} ${ps.text} ${ps.border}`}>{ticket.priority}</span>
        <p className={`text-[11px] font-semibold flex-1 min-w-0 truncate ${tp}`}>{ticket.title}</p>
        {isResolved && <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />}
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <User className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${tp}`}>{ticket.account}</span>
        <span className={`text-[9px] ${ts}`}>${(ticket.arr / 1000).toFixed(0)}K ARR</span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Clock className={`w-3 h-3 ${isUrgent ? (isDark ? 'text-red-400' : 'text-red-500') : ts}`} />
        <span className={`text-[9px] font-medium ${isUrgent ? (isDark ? 'text-red-400' : 'text-red-500') : isResolved ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ts}`}>SLA: {ticket.sla}</span>
      </div>
      <div className="mt-1.5">
        <p className={`text-[9px] ${ts} truncate`}>{ticket.lastActivity}</p>
        <p className={`text-[8px] mt-0.5 ${isDark ? 'text-white/30' : 'text-slate-500'}`}>{ticket.lastActivityTime} · {ticket.owner}</p>
      </div>
      {ticket.tags && (
        <div className="flex gap-1 mt-1.5">
          {ticket.tags.map(t => (
            <span key={t} className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-400/70' : 'bg-indigo-50 text-indigo-500'}`}>{t}</span>
          ))}
        </div>
      )}
      {/* CTA Actions row */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        {visibleActions.map(action => {
          const Icon = ACTION_ICONS[action] || Zap;
          const isEscalate = action === 'Escalate';
          return (
            <button key={action} onClick={() => onAction?.(ticket, action)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${
                isEscalate ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' :
                action === 'Reopen' ? `${isDark ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]' : 'bg-black/[0.03] text-slate-500 hover:bg-black/[0.06]'}` :
                `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`
              }`}
              data-testid={`ticket-action-${ticket.id}-${action.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-3 h-3" />{action}
            </button>
          );
        })}
        {/* Kebab menu */}
        <div className="relative ml-auto" ref={menuRef}>
          <button onClick={() => setMenuOpen(!menuOpen)}
            className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors ${menuOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : (isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]')}`}
            data-testid={`ticket-kebab-${ticket.id}`}
          >
            <MoreVertical className={`w-3.5 h-3.5 ${ts}`} />
          </button>
          {menuOpen && (
            <div className={`absolute bottom-full right-0 mb-1 rounded-lg py-1 z-20 min-w-[140px] ${isDark ? 'bg-[#0d1b2a] border border-white/[0.08]' : 'bg-white border border-black/[0.08]'} shadow-xl`}>
              {menuActions.map(action => {
                const Icon = ACTION_ICONS[action] || Zap;
                const isDelete = action === 'Delete';
                return (
                  <button key={action} onClick={() => { onAction?.(ticket, action); setMenuOpen(false); }}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-[10px] transition-colors ${isDelete ? (isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50') : `${tp} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}`}
                    data-testid={`ticket-menu-${ticket.id}-${action.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className={`w-3 h-3 ${isDelete ? '' : ts}`} />{action}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`ticket-insight-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Zira · {item.time}</p>
      </div>
    </div>
  );
};

const V4SupportTickets = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [stageFilter, setStageFilter] = useState(null);
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const stages = TICKETS_DATA.stages;
  const filteredTickets = stageFilter
    ? TICKETS_DATA.tickets.filter(t => t.stage === stageFilter)
    : TICKETS_DATA.tickets;

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || TICKETS_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCreateTicket = () => sendMessage('I want to create a new support ticket.', TICKETS_CHAT_RESPONSES.create);
  const handleAction = (ticket, action) => {
    sendMessage(`${action} on ticket "${ticket.title}" for ${ticket.account}.`, { text: `Got it. I'll ${action.toLowerCase()} for the "${ticket.title}" ticket (${ticket.account}).\n\nProcessing now... I'll update you once it's done. Is there anything else you'd like me to handle on this ticket?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Zira about tickets..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="tickets-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.zira} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Zira</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="tickets-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="tickets-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="tickets-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Tickets</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.zira} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const ticketsByStage = {};
  stages.forEach(s => { ticketsByStage[s] = filteredTickets.filter(t => t.stage === s); });

  return (
    <div className="flex flex-col h-full" data-testid="tickets-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Ticket className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Tickets</p>
            <span className={`text-[10px] ${ts}`}>{filteredTickets.length} total</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {[null, ...stages].map(s => (
                <button key={s || 'all'} onClick={() => setStageFilter(s)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-colors ${stageFilter === s ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
                  data-testid={`tickets-filter-${(s || 'all').toLowerCase().replace(/\s+/g, '-')}`}
                >{s || 'All'}</button>
              ))}
            </div>
            <button onClick={handleCreateTicket} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="tickets-create-btn">
              <Plus className="w-3.5 h-3.5" /> New Ticket
            </button>
          </div>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-x-auto">
        <div className="flex gap-3 p-4 h-full min-w-max">
          {stages.map(stage => {
            const stageTickets = ticketsByStage[stage] || [];
            const urgentCount = stageTickets.filter(t => t.slaUrgent || t.slaBreached).length;
            const isResolvedStage = stage === 'Resolved';
            return (
              <div key={stage} className="w-[280px] flex-shrink-0 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isResolvedStage ? (isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600') : (isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/70' : 'bg-black/[0.03] border-black/[0.06] text-slate-600')}`}>{stage}</span>
                    <span className={`text-[10px] ${ts}`}>{stageTickets.length}</span>
                  </div>
                  {urgentCount > 0 && (
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-500'}`}>
                      <Shield className="w-2.5 h-2.5 inline mr-0.5" />{urgentCount} SLA
                    </span>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto space-y-0">
                  {stageTickets.map(t => <TicketCard key={t.id} ticket={t} isDark={isDark} onAction={handleAction} />)}
                  {stageTickets.length === 0 && <p className={`text-[10px] ${ts} text-center py-4`}>No tickets</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`border-t ${border} px-5 py-3 flex-shrink-0`}>
        <div className="flex items-center gap-2 mb-2">
          <img src={AGENT_THUMBS.zira} alt="" className="w-4 h-4 rounded-full object-cover" />
          <p className={`text-[11px] font-semibold ${tp}`}>Zira's Insights</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {TICKETS_DATA.insights.slice(0, 2).map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SupportTickets;
